// Helpers for rendering the phrase "Live Agent Help" inside bot responses
// as a clickable inline button.
//
// Approach: pre-process the markdown source text so any occurrence of
// the phrase "Live Agent Help" becomes a markdown link with a sentinel
// href (LIVE_AGENT_HELP_HREF). Then override react-markdown's `a`
// component to detect that href and render the clickable button.
// This avoids relying on text-node overrides (which react-markdown v9
// doesn't expose) and works reliably with the existing ReactMarkdown
// + remarkGfm + rehypeRaw pipeline.

import React from "react";
import { Popup } from "semantic-ui-react";

export const LIVE_AGENT_HELP_HREF = "#__live_agent_help__";

// Tooltip shown on hover over the inline "Live Agent Help" link
// rendered inside bot responses. Kept in sync with the satisfaction-card
// tooltips in ChatBot.js / ExternalUser.js for a consistent experience.
const LIVE_AGENT_HELP_TOOLTIP = "Connect with a live support agent";

// Match "Live Agent Help" case-insensitively, tolerating extra whitespace.
const LIVE_AGENT_HELP_PHRASE = /\bLive\s+Agent\s+Help\b/gi;

// Inject the sentinel markdown link in place of every occurrence of the
// phrase. We split out fenced and inline code spans first so we don't
// rewrite the phrase if it ever appears inside code.
export const injectAgentHelpLink = (markdown) => {
  if (typeof markdown !== "string" || !markdown) return markdown;
  LIVE_AGENT_HELP_PHRASE.lastIndex = 0;
  if (!LIVE_AGENT_HELP_PHRASE.test(markdown)) return markdown;
  LIVE_AGENT_HELP_PHRASE.lastIndex = 0;

  // Even-indexed segments are non-code; odd-indexed are code. The capture
  // group covers fenced (```...```) and inline (`...`) code spans.
  const segments = markdown.split(/(```[\s\S]*?```|`[^`\n]*`)/g);
  return segments
    .map((seg, idx) => {
      if (idx % 2 === 1) return seg; // code segment, leave as-is
      return seg.replace(LIVE_AGENT_HELP_PHRASE, (m) => `[${m}](${LIVE_AGENT_HELP_HREF})`);
    })
    .join("");
};

// Build the `a` component override that turns the sentinel link into a
// clickable inline button. When `disabled` is true the same phrase is
// rendered as plain text (no styling, no click target) so the user can't
// trigger Live Agent Help again once a session is already active.
// Returns null if no handler is provided so the caller can decide to skip
// installing the override.
export const buildAgentHelpAnchorRenderer = (onAgentHelpClick, options = {}) => {
  if (typeof onAgentHelpClick !== "function") return null;
  const { isDisabled } = options;
  // eslint-disable-next-line react/display-name
  return ({ node, href, children, ...props }) => {
    if (href === LIVE_AGENT_HELP_HREF) {
      // Resolve disabled lazily so the latest value is read on every
      // render without needing to rebuild the renderer.
      const disabled = typeof isDisabled === "function" ? !!isDisabled() : !!isDisabled;
      if (disabled) {
        return (
          <Popup
            inverted
            position="top center"
            content={LIVE_AGENT_HELP_TOOLTIP}
            trigger={
              <span
                className="inline-live-agent-help inline-live-agent-help-disabled"
                aria-disabled="true"
              >
                {children}
              </span>
            }
          />
        );
      }
      return (
        <Popup
          inverted
          position="top center"
          content={LIVE_AGENT_HELP_TOOLTIP}
          trigger={
            <span
              role="button"
              tabIndex={0}
              className="inline-live-agent-help"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAgentHelpClick();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onAgentHelpClick();
                }
              }}
              data-cy="inline-live-agent-help-cy"
            >
              {children}
            </span>
          }
        />
      );
    }
    // Default anchor rendering for normal links — open in a new tab with
    // safe rel attributes.
    return (
      <a href={href} {...props} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };
};
